import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Header } from './ai-scan/Header';
import { AnalyzingView } from './ai-scan/AnalyzingView';
import { EditorView } from './ai-scan/EditorView';
import { StorefrontView } from './ai-scan/StorefrontView';
import { BulkCameraSession } from './BulkCameraSession';
import { AppState, ProductData } from '../../types';
import { analyzeProductImage } from '../../services/geminiService';
import { ThemeContext } from '../context/themeContext';
import api from '../../utils/api'
import { API_CONFIG } from '../../utils/apiConfig';
import { showToast } from '../../utils/helpers';
import { useNavigate } from 'react-router-dom';

interface ScanProductProps {
  backButton: React.ReactNode;
  onRequestBack?: () => void;
}

const ScanProduct: React.FC<ScanProductProps> = ({ backButton, onRequestBack }) => {
  const [state, setState] = useState<AppState>(AppState.IDLE);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [productData, setProductData] = useState<ProductData | null>(null);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const handleImageSelect = useCallback(async (file: File) => {
    const reader = new FileReader();
    
    reader.onloadend = async () => {
      const base64String = reader.result as string;
      setImageSrc(base64String);
      setState(AppState.ANALYZING);

      try {
        // Call Gemini API
        const analyzedData = await analyzeProductImage(base64String);
        
        // Merge the analyzed data with the captured image
        const completeData: ProductData = {
            title: analyzedData.title || '',
            description: analyzedData.description || '',
            price: analyzedData.price || 0,
            currency: 'NGN',
            category: analyzedData.category || 'General',
            tags: analyzedData.tags || [],
            images: [base64String],
            colors: analyzedData.colors || [],
            sizes: analyzedData.sizes || [],
            materials: analyzedData.materials || [],
            styles: analyzedData.styles || [],
            patterns: analyzedData.patterns || [],
            brand: analyzedData.brand || 'Generic',
            condition: (analyzedData.condition as any) || 'New',
            isDigital: analyzedData.isDigital || false,
            digitalProductLink: '',
            digitalFileName: '',
            stock: 1,
            variants: [],
            dimensions: analyzedData.dimensions || '',
            weight: analyzedData.weight || '',
            deliveryOption: 'Standard Delivery',
            deliveryTimeline: '3',
            returnPolicy: '7',
            fileSize: analyzedData.digitalFileSize,
            fileType: analyzedData.digitalFileType
        };

        setProductData(completeData);
        
        setTimeout(() => {
            setState(AppState.EDITING);
        }, 2500);

      } catch (error) {
        console.error("Analysis failed", error);
        setState(AppState.IDLE);
      }
    };

    reader.readAsDataURL(file);
  }, []);

  // Helper function to convert base64 to blob
  const dataURLtoBlob = (dataURL: string) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new Blob([u8arr], { type: mime });
  };

  // Upload images to backend first, then save product with URLs
  const uploadImages = async (images: string[]): Promise<string[]> => {
    const uploadedUrls: string[] = [];
    
    for (const image of images) {
      if (image.startsWith('data:')) {
        try {
          const blob = dataURLtoBlob(image);
          const formData = new FormData();
          formData.append('file', blob, 'product_image.jpg');
          
          const response = await api.post(API_CONFIG.PRODUCTS.UPLOAD, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          
          if (response.data.success) {
            const fileUrl = response.data.url || response.data.fileUrl || response.data.secure_url;
            uploadedUrls.push(fileUrl);
            showToast('Image uploaded successfully!', 'success');
          } else {
            throw new Error(response.data.message || 'Image upload failed');
          }
        } catch (error) {
          console.error('Error uploading image:', error);
          throw new Error('Failed to upload product images');
        }
      } else {
        // Already a URL, just add it
        uploadedUrls.push(image);
      }
    }
    
    return uploadedUrls;
  };

  const handlePublish = async (finalData: ProductData) => {
    try {
      setState(AppState.PUBLISHING);
      
      // First, upload all images to get URLs
      let imageUrls: string[] = [];
      if (finalData.images && finalData.images.length > 0) {
        imageUrls = await uploadImages(finalData.images);
      }
      
      // Now prepare the product data with image URLs
      const submitData = new FormData();
      
      // Basic product information
      submitData.append('name', finalData.title);
      submitData.append('description', finalData.description);
      submitData.append('price', finalData.price.toString());
      submitData.append('category', finalData.category);
      submitData.append('delivery_timeline', finalData.deliveryTimeline);
      submitData.append('inventory', finalData.stock.toString());
      submitData.append('brand', finalData.brand);
      submitData.append('condition', finalData.condition);
      submitData.append('return_policy', finalData.returnPolicy);
      submitData.append('weight', finalData.weight);
      submitData.append('dimensions', finalData.dimensions);
      submitData.append('listing_type', 'product');
      
      // Add image URLs
      imageUrls.forEach(url => {
        submitData.append('image_urls', url);
      });
      
      // Digital product fields
      if (finalData.isDigital) {
        submitData.append('is_digital', 'true');
        submitData.append('digital_product_type', finalData.digitalProductType || 'other');
        submitData.append('allowed_downloads', finalData.allowedDownloads?.toString() || '3');
        submitData.append('download_expiry_days', finalData.downloadExpiryDays?.toString() || '30');
        submitData.append('watermark_enabled', finalData.watermarkEnabled ? 'true' : 'false');
        
        if (finalData.digitalProductLink) {
          submitData.append('digital_file_url', finalData.digitalProductLink);
        }
        if (finalData.cloudinaryPublicId) {
          submitData.append('cloudinary_public_id', finalData.cloudinaryPublicId);
        }
        if (finalData.fileSize) {
          submitData.append('file_size', finalData.fileSize.toString());
        }
        if (finalData.fileType) {
          submitData.append('file_type', finalData.fileType);
        }
      } else {
        submitData.append('is_digital', 'false');
      }
      
      // Convert attributes to variations
      const attributeVariations = [];
      
      // Handle colors
      if (finalData.colors && finalData.colors.length > 0) {
        finalData.colors.forEach(color => {
          attributeVariations.push({
            type: 'Color',
            value: color,
            price: 0,
            inventory: Math.max(1, Math.floor(finalData.stock / finalData.colors.length))
          });
        });
      }
      
      // Handle sizes
      if (finalData.sizes && finalData.sizes.length > 0) {
        finalData.sizes.forEach(size => {
          attributeVariations.push({
            type: 'Size',
            value: size,
            price: 0,
            inventory: Math.max(1, Math.floor(finalData.stock / finalData.sizes.length))
          });
        });
      }
      
      // Handle materials
      if (finalData.materials && finalData.materials.length > 0) {
        finalData.materials.forEach(material => {
          attributeVariations.push({
            type: 'Material',
            value: material,
            price: 0,
            inventory: Math.max(1, Math.floor(finalData.stock / finalData.materials.length))
          });
        });
      }
      
      // Handle styles
      if (finalData.styles && finalData.styles.length > 0) {
        finalData.styles.forEach(style => {
          attributeVariations.push({
            type: 'Style',
            value: style,
            price: 0,
            inventory: Math.max(1, Math.floor(finalData.stock / finalData.styles.length))
          });
        });
      }
      
      // Handle patterns
      if (finalData.patterns && finalData.patterns.length > 0) {
        finalData.patterns.forEach(pattern => {
          attributeVariations.push({
            type: 'Pattern',
            value: pattern,
            price: 0,
            inventory: Math.max(1, Math.floor(finalData.stock / finalData.patterns.length))
          });
        });
      }
      
      // Handle explicit variants
      if (finalData.variants && finalData.variants.length > 0) {
        finalData.variants.forEach(variant => {
          const type = Object.keys(variant.attributes)[0] || 'Variant';
          const value = Object.values(variant.attributes)[0] || variant.name;
          attributeVariations.push({
            type: type,
            value: value,
            price: variant.price - finalData.price,
            inventory: variant.stock
          });
        });
      }
      
      if (attributeVariations.length > 0) {
        submitData.append('variations', JSON.stringify(attributeVariations));
      }
      
      // Tags
      if (finalData.tags && finalData.tags.length > 0) {
        submitData.append('tags', JSON.stringify(finalData.tags));
      }
      
      // Delivery options for physical products
      if (!finalData.isDigital) {
        submitData.append('available_for_pickup', finalData.availableForPickup !== false ? 'true' : 'false');
        submitData.append('available_for_delivery', finalData.availableForDelivery !== false ? 'true' : 'false');

        if (finalData.availableForDelivery !== false) {
          submitData.append('lagos_delivery_fee', (finalData.lagosDeliveryFee || 0).toString());
          submitData.append('outside_lagos_delivery_fee', (finalData.outsideLagosDeliveryFee || 0).toString());
        }

        submitData.append('accept_negotiations', finalData.acceptNegotiations ? 'true' : 'false');
        if (finalData.acceptNegotiations) {
          submitData.append('min_price', (finalData.minPrice || 0).toString());
          submitData.append('max_price', (finalData.maxPrice || 0).toString());
        }

        submitData.append('upfront_payment_percentage', (finalData.upfrontPaymentPercentage || 0).toString());
      }
      
      console.log('Submitting product with:', {
        name: finalData.title,
        images: imageUrls.length,
        isDigital: finalData.isDigital,
        variations: attributeVariations.length
      });
      
      // Make API call to save product
      const response = await api.post('/save-product', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      if (response.data.success) {
        showToast('Product created successfully!', 'success');
        setProductData(finalData);
        window.location.href = '/store#products';
        // navigate("/store#products");
      } else {
        throw new Error(response.data.message || 'Failed to create product');
      }
      
    } catch (error: any) {
      console.error('Error creating product:', error);
      showToast(
        error.response?.data?.message || 
        error.message || 
        'Failed to create product', 
        'error'
      );
      setState(AppState.EDITING);
    }
  };

  const handleReset = () => {
    setState(AppState.IDLE);
    setImageSrc(null);
    setProductData(null);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <div className={theme === 'dark' ? 'dark' : ''}>
        {/* Updated background and text colors to match CreateProduct */}
        <div className="min-h-screen bg-white text-gray-900 font-sans selection:bg-blue-500/30 flex flex-col transition-colors duration-300">
          <main className="flex-1 flex flex-col relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex-1 flex flex-col h-full w-full items-center pt-8 md:pt-12">
                {backButton}
                
                {state === AppState.IDLE && (
                    <div className="w-full h-full min-h-[400px]">
                      <BulkCameraSession
                        singleMode
                        maxProducts={1}
                        onFilesReady={(files) => handleImageSelect(files[0])}
                        onBack={onRequestBack || (() => {})}
                      />
                    </div>
                )}

                {state === AppState.ANALYZING && imageSrc && (
                    <AnalyzingView imageSrc={imageSrc} />
                )}

                {state === AppState.EDITING && productData && (
                    <EditorView 
                        initialData={productData} 
                        onConfirm={handlePublish} 
                    />
                )}

                {state === AppState.PUBLISHING && (
                    <div className="flex flex-col items-center justify-center p-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                      <p className="text-gray-600">Publishing your product...</p>
                    </div>
                )}
            </div>
          </main>
        </div>
      </div>
    </ThemeContext.Provider>
  );
};

export default ScanProduct;